import { computed } from 'vue';
import {
    BriefcaseBusiness,
    CircleDot,
    FileText,
    MessageSquare,
    Receipt,
    Rocket,
    ShieldCheck,
    Sparkles,
    Trophy,
} from 'lucide-vue-next';
import { toMillis } from '~/lib/timestamps';
import { isProjectOperational } from '~/lib/projectLifecycle';
import type { Approval, Client, ClientContact, ClientPipelineStage, Invoice, OnboardingRun, Project, Task } from '~/types/models';

type WorkspaceRef<T> = { value: T };

type CommunicationEntry = {
    id: string;
    direction: 'inbound' | 'outbound';
    channel: 'app' | 'email' | 'whatsapp' | 'call' | 'note';
    title: string;
    actorName?: string;
    body?: string;
    at: unknown;
};

interface OverviewContext {
    linkedClient: WorkspaceRef<Client | null>;
    clientPipelineStage: WorkspaceRef<ClientPipelineStage>;
    highlightedRun: WorkspaceRef<OnboardingRun | null>;
    readyToStartOnboarding: WorkspaceRef<boolean>;
    waitingClientTasks: WorkspaceRef<Task[]>;
    pendingApprovals: WorkspaceRef<Approval[]>;
    daysInOnboarding: WorkspaceRef<number | null>;
    tenantPath: (input: any) => string;
    daysSinceLastContact: WorkspaceRef<number | null>;
    selectedOverviewAttentionCardId: WorkspaceRef<string | null>;
    projectLinkItems: WorkspaceRef<Array<{ project: Project; href: string | null }>>;
    relatedTasks: WorkspaceRef<Task[]>;
    relatedApprovals: WorkspaceRef<Approval[]>;
    projectStatsById: WorkspaceRef<Record<string, { taskTotal?: number; taskCompleted?: number }>>;
    relatedProjects: WorkspaceRef<Project[]>;
    relatedRuns: WorkspaceRef<OnboardingRun[]>;
    clientActivities: WorkspaceRef<any[]>;
    communicationTimeline: WorkspaceRef<CommunicationEntry[]>;
    currentRunProject: WorkspaceRef<Project | null>;
    recommendedOnboardingTemplate: WorkspaceRef<{ name?: string } | null>;
    primaryClientContact: WorkspaceRef<ClientContact | null>;
    lifecycleStatusLabel: (client: Client | null | undefined) => string;
    nextMoveLabel: () => string;
    relatedInvoices: WorkspaceRef<Invoice[]>;
    outstandingInvoices: WorkspaceRef<Invoice[]>;
    overdueInvoicesCount: WorkspaceRef<number>;
    clientCurrency: WorkspaceRef<string>;
    canRespondApprovals: WorkspaceRef<boolean>;
    formatDate: (value?: unknown) => string;
    formatRelativeTime: (value?: unknown, fallback?: string) => string;
    formatMoney: (amount: number, currency?: string) => string;
    projectHref: (project: Project) => string | null;
    communicationChannelLabel: (channel: CommunicationEntry['channel']) => string;
    communicationChannelIcon: (channel: CommunicationEntry['channel']) => any;
    communicationChannelBadgeClass: (channel: CommunicationEntry['channel']) => string;
    isPendingClientTaskWork: (task: Task) => boolean;
}

export type ClientOverviewAttentionAction = {
    kind: 'tab' | 'href' | 'dialog' | 'task' | 'approval';
    label: string;
    tab?: string;
    href?: string;
    taskId?: string;
    approvalId?: string;
    dialog?: 'controls' | 'notes' | 'onboarding';
};

type ClientOverviewAttentionCard = {
    id: string;
    title: string;
    detail: string;
    icon: any;
    accentClass: string;
    action: ClientOverviewAttentionAction;
};

type ClientOverviewAttentionDialogItem = {
    id: string;
    title: string;
    presentation?: 'summary' | 'thread' | 'approval' | 'message' | 'invoice' | 'onboarding';
    subtitle?: string;
    summary?: string;
    detailRows?: string[];
    detailFields?: Array<{ label: string; value: string }>;
    metaChips?: Array<{ label: string; tone?: 'default' | 'muted' | 'warning' }>;
    badge?: string;
    thread?: {
        promptMessage?: string;
        responseStatus?: string;
        thread?: Task['clientResponseThread'];
        emptyState?: string;
        showPromptMessage?: boolean;
    };
    approval?: Approval;
    message?: {
        title?: string;
        actorName?: string;
        channelLabel?: string;
        body: string;
        timestamp?: unknown;
    };
    invoice?: {
        invoice: Invoice;
        projectName?: string;
        currency?: string;
    };
    onboarding?: {
        mode?: 'requirement' | 'handoff';
        summary: string;
        statusLabel?: string;
        stageLabel?: string;
        projectName?: string;
        requirementType?: string;
        templateName?: string;
        linkedProjectsLabel?: string;
    };
    primaryAction?: ClientOverviewAttentionAction;
    secondaryAction?: ClientOverviewAttentionAction;
};

type ClientOverviewAttentionDialogState = {
    title: string;
    description: string;
    emptyTitle: string;
    emptyDescription: string;
    items: ClientOverviewAttentionDialogItem[];
};

export type ClientOverviewActivityItem = {
    id: string;
    title: string;
    subtitle?: string;
    at: unknown;
    icon: any;
    accentClass: string;
};

type ClientOverviewProjectCard = {
    id: string;
    name: string;
    href: string;
    stageLabel: string;
    progressLabel: string;
    nextAction: string;
    approvalsLabel: string;
    blockersLabel: string;
    attentionScore: number;
};

type CommercialFollowUpMeta = {
    title: string;
    detail: string;
    badge: string;
    primaryAction: ClientOverviewAttentionAction;
    secondaryAction: ClientOverviewAttentionAction;
};

export function useClientWorkspaceOverview(context: OverviewContext) {
    const {
        linkedClient,
        clientPipelineStage,
        highlightedRun,
        readyToStartOnboarding,
        waitingClientTasks,
        pendingApprovals,
        daysInOnboarding,
        tenantPath,
        daysSinceLastContact,
        selectedOverviewAttentionCardId,
        projectLinkItems,
        relatedTasks,
        relatedApprovals,
        projectStatsById,
        relatedProjects,
        relatedRuns,
        clientActivities,
        communicationTimeline,
        currentRunProject,
        recommendedOnboardingTemplate,
        primaryClientContact,
        lifecycleStatusLabel,
        nextMoveLabel,
        relatedInvoices,
        outstandingInvoices,
        overdueInvoicesCount,
        clientCurrency,
        canRespondApprovals,
        formatDate,
        formatRelativeTime,
        formatMoney,
        projectHref,
        communicationChannelLabel,
        communicationChannelIcon,
        communicationChannelBadgeClass,
        isPendingClientTaskWork,
    } = context;

    function formatHumanValue(value: unknown): string {
        if (value === undefined || value === null || value === '' || value === 'unset') return 'not set';
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) return 'not set';
            if (/^contact_[a-z0-9]+$/i.test(trimmed)) return 'primary contact';
            if (trimmed.startsWith('<')) return 'rich text content';
            return trimmed.replace(/_/g, ' ');
        }
        if (typeof value === 'boolean') return value ? 'enabled' : 'disabled';
        if (typeof value === 'number') return String(value);
        return 'updated details';
    }

    function plainTextFromHtml(value: string): string {
        return String(value || '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/\s+/g, ' ')
            .trim();
    }

const commercialFollowUpMeta = computed<CommercialFollowUpMeta | null>(() => {
    const currentClient = linkedClient.value;
    if (!currentClient || highlightedRun.value || readyToStartOnboarding.value) {
        return null;
    }

    switch (clientPipelineStage.value) {
        case 'new_enquiry':
            return {
                title: 'Qualify the enquiry',
                detail: 'This is a fresh enquiry and needs a first response, qualification, and a clear next step.',
                badge: 'New enquiry',
                primaryAction: { kind: 'dialog' as const, label: 'Open lifecycle controls', dialog: 'controls' },
                secondaryAction: { kind: 'tab' as const, label: 'Open comms workspace', tab: 'comms' },
            };
        case 'contacted':
            return {
                title: 'Continue qualification',
                detail: 'The first touch has happened. Capture what matters and decide whether this moves forward.',
                badge: 'Qualification',
                primaryAction: { kind: 'dialog' as const, label: 'Open lifecycle controls', dialog: 'controls' },
                secondaryAction: { kind: 'tab' as const, label: 'Open comms workspace', tab: 'comms' },
            };
        case 'qualified':
            return {
                title: 'Shape the next commercial step',
                detail: 'This lead is qualified and needs a deliberate proposal, scope, or commercial next move.',
                badge: 'Qualified',
                primaryAction: { kind: 'dialog' as const, label: 'Open lifecycle controls', dialog: 'controls' },
                secondaryAction: { kind: 'tab' as const, label: 'Open comms workspace', tab: 'comms' },
            };
        case 'proposal_sent':
            return {
                title: 'Follow up on the proposal',
                detail: 'A proposal is already out. Keep the thread warm and close the loop on questions or approval.',
                badge: 'Proposal sent',
                primaryAction: { kind: 'dialog' as const, label: 'Open lifecycle controls', dialog: 'controls' },
                secondaryAction: { kind: 'tab' as const, label: 'Open comms workspace', tab: 'comms' },
            };
        case 'negotiation':
            return {
                title: 'Close the deal',
                detail: 'Commercial details are in play. Keep the next decision visible so this does not stall.',
                badge: 'Negotiation',
                primaryAction: { kind: 'dialog' as const, label: 'Open lifecycle controls', dialog: 'controls' },
                secondaryAction: { kind: 'tab' as const, label: 'Open comms workspace', tab: 'comms' },
            };
        default:
            return null;
    }
});
const overviewAttentionCards = computed<ClientOverviewAttentionCard[]>(() => {
    const cards: ClientOverviewAttentionCard[] = [];

    if (waitingClientTasks.value.length > 0) {
        cards.push({
            id: 'waiting-client',
            title: 'Awaiting client response',
            detail: `${waitingClientTasks.value.length} task${waitingClientTasks.value.length === 1 ? '' : 's'} cannot move until the client replies or uploads what is needed.`,
            icon: MessageSquare,
            accentClass: 'text-amber-600 dark:text-amber-300',
            action: { kind: 'tab', label: 'Open delivery queue', tab: 'delivery' },
        });
    }

    if (pendingApprovals.value.length > 0) {
        cards.push({
            id: 'pending-approvals',
            title: 'Pending approvals',
            detail: `${pendingApprovals.value.length} approval${pendingApprovals.value.length === 1 ? '' : 's'} are still waiting and could slow delivery.`,
            icon: ShieldCheck,
            accentClass: 'text-amber-600 dark:text-amber-300',
            action: { kind: 'tab', label: 'Review approvals', tab: 'delivery' },
        });
    }

    if (highlightedRun.value || readyToStartOnboarding.value) {
        cards.push({
            id: 'onboarding',
            title: highlightedRun.value ? 'Active onboarding' : 'Onboarding ready',
            detail: highlightedRun.value
                ? `${highlightedRun.value.templateName || 'Onboarding run'} is live${daysInOnboarding.value != null ? ` and ${daysInOnboarding.value} day${daysInOnboarding.value === 1 ? '' : 's'} in` : ''}.`
                : 'This client is won and ready to move into a structured onboarding handoff.',
            icon: Rocket,
            accentClass: 'text-sky-600 dark:text-sky-300',
            action: highlightedRun.value
                ? { kind: 'href', label: 'Open onboarding', href: tenantPath('/onboarding') }
                : { kind: 'dialog', label: 'Start onboarding', dialog: 'onboarding' },
        });
    }

    if (commercialFollowUpMeta.value) {
        cards.push({
            id: 'commercial-follow-up',
            title: commercialFollowUpMeta.value.title,
            detail: commercialFollowUpMeta.value.detail,
            icon: Trophy,
            accentClass: 'text-violet-600 dark:text-violet-300',
            action: commercialFollowUpMeta.value.primaryAction,
        });
    }

    if ((daysSinceLastContact.value || 0) >= 14) {
        cards.push({
            id: 'stale-contact',
            title: 'No recent contact',
            detail: `This client has been quiet for ${daysSinceLastContact.value} days and may need a deliberate follow-up.`,
            icon: CircleDot,
            accentClass: 'text-violet-600 dark:text-violet-300',
            action: { kind: 'tab', label: 'Open comms', tab: 'comms' },
        });
    }

    if (overdueInvoicesCount.value > 0) {
        cards.push({
            id: 'overdue-invoice',
            title: 'Outstanding payment risk',
            detail: `${overdueInvoicesCount.value} overdue invoice${overdueInvoicesCount.value === 1 ? '' : 's'} may need finance follow-up.`,
            icon: Receipt,
            accentClass: 'text-amber-600 dark:text-amber-300',
            action: { kind: 'tab', label: 'Open finance', tab: 'finance' },
        });
    }

    if (!cards.length) {
        cards.push({
            id: 'steady',
            title: 'Client is steady',
            detail: 'Nothing urgent is calling for intervention right now. Use the previews below to decide what deserves the next touch.',
            icon: Sparkles,
            accentClass: 'text-emerald-600 dark:text-emerald-300',
            action: { kind: 'dialog', label: 'Manage client', dialog: 'controls' },
        });
    }

    return cards.slice(0, 4);
});
const overviewActiveProjects = computed<ClientOverviewProjectCard[]>(() => projectLinkItems.value
    .filter(item => isProjectOperational(item.project.status))
    .map((item) => {
        const project = item.project;
        const href = item.href || tenantPath('/projects');
        const projectTasks = relatedTasks.value.filter((task) => task.projectId === project.id);
        const openTaskCount = projectTasks.filter((task) => !['done', 'completed'].includes(String(task.status || '').toLowerCase())).length;
        const overdueTaskCount = projectTasks.filter((task) => {
            const dueAt = toMillis(task.dueDate);
            return !['done', 'completed'].includes(String(task.status || '').toLowerCase()) && dueAt > 0 && dueAt < Date.now();
        }).length;
        const waitingTaskCount = projectTasks.filter((task) => (
            task.clientVisible
            && isPendingClientTaskWork(task)
        )).length;
        const projectApprovalsPending = relatedApprovals.value.filter((approval) => approval.projectId === project.id && approval.status === 'pending').length;
        const stats = projectStatsById.value[project.id];
        const nextAction = waitingTaskCount > 0
            ? `${waitingTaskCount} waiting on client`
            : projectApprovalsPending > 0
                ? `${projectApprovalsPending} approval${projectApprovalsPending === 1 ? '' : 's'} waiting`
                : overdueTaskCount > 0
                    ? `${overdueTaskCount} overdue task${overdueTaskCount === 1 ? '' : 's'}`
                    : openTaskCount > 0
                        ? `${openTaskCount} open task${openTaskCount === 1 ? '' : 's'}`
                        : 'No blockers right now';

        return {
            id: project.id,
            name: project.name || 'Untitled project',
            href,
            stageLabel: project.stage || formatHumanValue(project.status || 'active'),
            progressLabel: stats?.taskTotal ? `${stats.taskCompleted}/${stats.taskTotal} tasks complete` : 'No tasks created yet',
            nextAction,
            approvalsLabel: projectApprovalsPending > 0 ? `${projectApprovalsPending} approvals waiting` : 'No approvals blocked',
            blockersLabel: overdueTaskCount > 0 ? `${overdueTaskCount} overdue tasks` : waitingTaskCount > 0 ? `${waitingTaskCount} waiting on client` : 'No blockers flagged',
            attentionScore: (waitingTaskCount * 4) + (projectApprovalsPending * 3) + (overdueTaskCount * 2) + openTaskCount,
        };
    })
    .sort((left, right) => right.attentionScore - left.attentionScore || left.name.localeCompare(right.name))
    .slice(0, 3));
function summarizedOverviewText(value: unknown, fallback: string): string {
    const text = plainTextFromHtml(String(value || '')).trim();
    return text || fallback;
}

function overviewTaskThreadPresentation(task: Task) {
    return {
        promptMessage: summarizedOverviewText(
            task.clientResponsePrompt || task.description,
            'The team is waiting for the client to reply or upload what is needed before work can continue.',
        ),
        responseStatus: task.clientResponseStatus || 'waiting',
        thread: Array.isArray(task.clientResponseThread) ? task.clientResponseThread : [],
        emptyState: 'No client response thread yet. Once the client replies, the conversation will show here.',
        showPromptMessage: true,
    };
}

function overviewTaskMetaChips(task: Task) {
    return [
        {
            label: task.dueDate ? `Due ${formatDate(task.dueDate)}` : 'No due date set',
            tone: task.dueDate ? 'default' as const : 'warning' as const,
        },
        {
            label: task.clientResponseUpdatedAt
                ? `Last client update ${formatRelativeTime(task.clientResponseUpdatedAt, 'recently')}`
                : 'No client response logged',
            tone: task.clientResponseUpdatedAt ? 'muted' as const : 'warning' as const,
        },
    ];
}

function relatedProjectNameById(projectId?: string | null): string {
    const id = String(projectId || '').trim();
    if (!id) return 'Project';
    return relatedProjects.value.find((project) => project.id === id)?.name || 'Project';
}

function projectTaskHref(task: Task): string | null {
    const projectId = String(task.projectId || '').trim();
    if (!projectId || !task.id) return null;
    return tenantPath({
        path: `/projects/${projectId}`,
        query: {
            tab: 'tasks',
            taskId: task.id,
        },
    });
}

function projectApprovalHref(approval: Approval): string | null {
    const projectId = String(approval.projectId || '').trim();
    if (!projectId || !approval.id) return null;
    return tenantPath({
        path: `/projects/${projectId}`,
        query: {
            tab: 'tasks',
            approvalId: approval.id,
        },
    });
}

function projectFinanceHref(invoice: Invoice): string | null {
    const projectId = String(invoice.projectId || '').trim();
    if (!projectId) return null;
    return tenantPath({
        path: `/projects/${projectId}`,
        query: {
            tab: 'finance',
        },
    });
}

function onboardingStageLabel(run: OnboardingRun): string {
    return run.stages.find((stage) => stage.id === run.currentStageId)?.label
        || formatHumanValue(run.currentStageId || 'Current stage');
}

const overviewAttentionDialogState = computed<ClientOverviewAttentionDialogState | null>(() => {
    const cardId = selectedOverviewAttentionCardId.value;
    if (!cardId) return null;

    if (cardId === 'waiting-client') {
        return {
            title: 'Awaiting client response',
            description: 'Review the work that is blocked on client input. Each item can be opened here or taken straight into the project context.',
            emptyTitle: 'No client-response tasks right now',
            emptyDescription: 'Once this client has client-facing tasks waiting on a reply, they will show up here.',
            items: [...waitingClientTasks.value]
                .sort((left, right) => toMillis(left.dueDate) - toMillis(right.dueDate))
                .map((task) => ({
                    id: task.id,
                    title: task.title || 'Client response needed',
                    presentation: 'thread' as const,
                    subtitle: `${relatedProjectNameById(task.projectId) || task.client || 'Client task'} • ${formatHumanValue(task.status || 'open')}`,
                    thread: overviewTaskThreadPresentation(task),
                    metaChips: overviewTaskMetaChips(task),
                    badge: 'Waiting on client',
                    primaryAction: { kind: 'task', label: 'Open task here', taskId: task.id },
                    secondaryAction: projectTaskHref(task)
                        ? { kind: 'href', label: 'Open in project', href: projectTaskHref(task) || undefined }
                        : { kind: 'tab', label: 'Open delivery queue', tab: 'delivery' },
                })),
        };
    }

    if (cardId === 'pending-approvals') {
        return {
            title: 'Pending approvals',
            description: 'These approvals are waiting for a decision and can slow delivery until someone responds.',
            emptyTitle: 'No pending approvals right now',
            emptyDescription: 'New approvals will appear here when they need a decision.',
            items: [...pendingApprovals.value]
                .sort((left, right) => toMillis(left.createdAt) - toMillis(right.createdAt))
                .map((approval) => ({
                    id: approval.id,
                    title: approval.stageName || 'Project approval',
                    presentation: 'approval' as const,
                    approval,
                    metaChips: [
                        {
                            label: `${approval.projectName || relatedProjectNameById(approval.projectId)} • requested ${formatRelativeTime(approval.createdAt, 'recently')}`,
                            tone: 'muted' as const,
                        },
                    ],
                    primaryAction: canRespondApprovals.value
                        ? { kind: 'approval', label: 'Respond here', approvalId: approval.id }
                        : { kind: 'href', label: 'Open in project', href: projectApprovalHref(approval) || undefined },
                    secondaryAction: canRespondApprovals.value && projectApprovalHref(approval)
                        ? { kind: 'href', label: 'Open in project', href: projectApprovalHref(approval) || undefined }
                        : { kind: 'tab', label: 'Open delivery queue', tab: 'delivery' },
                })),
        };
    }

    if (cardId === 'onboarding') {
        if (highlightedRun.value) {
            const activeRequirements = highlightedRun.value.requirements
                .filter((requirement) => !['completed', 'skipped'].includes(String(requirement.status || '').toLowerCase()))
                .slice(0, 5);

            return {
                title: 'Active onboarding',
                description: 'Keep the current onboarding run visible here, with the next open requirements ready to review.',
                emptyTitle: 'Onboarding is live',
                emptyDescription: 'The run is active, but there are no open requirement details to show yet.',
                items: activeRequirements.map((requirement) => ({
                    id: requirement.id,
                    title: requirement.title,
                    presentation: 'onboarding' as const,
                    onboarding: {
                        mode: 'requirement' as const,
                        summary: summarizedOverviewText(
                            requirement.description,
                            'This requirement is part of the current onboarding flow and still needs attention.',
                        ),
                        statusLabel: formatHumanValue(requirement.status || 'pending'),
                        stageLabel: onboardingStageLabel(highlightedRun.value!),
                        projectName: currentRunProject.value?.name || highlightedRun.value?.projectName || 'Client onboarding',
                        requirementType: formatHumanValue(requirement.type || 'task'),
                    },
                    metaChips: [
                        {
                            label: requirement.dueDate ? `Due ${formatDate(requirement.dueDate)}` : 'No due date set',
                            tone: requirement.dueDate ? 'default' as const : 'warning' as const,
                        },
                    ],
                    primaryAction: { kind: 'href', label: 'Open onboarding', href: tenantPath('/onboarding') },
                    secondaryAction: currentRunProject.value && projectHref(currentRunProject.value)
                        ? { kind: 'href', label: 'Open linked project', href: projectHref(currentRunProject.value) || undefined }
                        : undefined,
                })),
            };
        }

        return {
            title: 'Onboarding ready',
            description: 'This client is ready to move into a structured handoff. Start the run here, or open the linked project if you want more context first.',
            emptyTitle: 'No onboarding actions yet',
            emptyDescription: 'When this client is ready for onboarding, the next move will appear here.',
            items: [{
                id: 'start-onboarding',
                title: 'Start onboarding handoff',
                presentation: 'onboarding',
                onboarding: {
                    mode: 'handoff',
                    summary: summarizedOverviewText(
                        linkedClient.value?.lifecycle?.notes,
                        'This client is won and ready to move into a structured onboarding handoff.',
                    ),
                    statusLabel: 'Ready',
                    templateName: recommendedOnboardingTemplate.value?.name || 'Recommended onboarding template',
                    projectName: relatedProjects.value[0]?.name,
                    linkedProjectsLabel: relatedProjects.value.length > 0
                        ? `${relatedProjects.value.length} linked project${relatedProjects.value.length === 1 ? '' : 's'} available`
                        : 'No linked projects yet',
                },
                metaChips: [
                    {
                        label: clientPipelineStage.value === 'won'
                            ? 'Client is ready to move into delivery'
                            : 'Review client readiness before starting',
                        tone: 'muted',
                    },
                ],
                primaryAction: { kind: 'dialog', label: 'Start onboarding', dialog: 'onboarding' },
                secondaryAction: relatedProjects.value[0] && projectHref(relatedProjects.value[0])
                    ? { kind: 'href', label: 'Open latest project', href: projectHref(relatedProjects.value[0]) || undefined }
                    : undefined,
            }],
        };
    }

    if (cardId === 'commercial-follow-up' && commercialFollowUpMeta.value) {
        return {
            title: commercialFollowUpMeta.value.title,
            description: 'Keep one clear commercial next move visible so new enquiries and live opportunities do not drift.',
            emptyTitle: 'No commercial follow-up actions',
            emptyDescription: 'The next commercial suggestion will appear here when this client needs one.',
            items: [{
                id: 'commercial-follow-up:summary',
                title: commercialFollowUpMeta.value.title,
                presentation: 'summary',
                summary: commercialFollowUpMeta.value.detail,
                detailFields: [
                    {
                        label: 'Lifecycle',
                        value: lifecycleStatusLabel(linkedClient.value),
                    },
                    {
                        label: 'Recommended next move',
                        value: nextMoveLabel(),
                    },
                ],
                metaChips: [
                    {
                        label: commercialFollowUpMeta.value.badge,
                        tone: 'warning',
                    },
                    {
                        label: primaryClientContact.value?.email
                            ? `Primary contact: ${primaryClientContact.value.email}`
                            : 'Primary contact route still needs attention',
                        tone: primaryClientContact.value?.email ? 'muted' : 'warning',
                    },
                ],
                badge: commercialFollowUpMeta.value.badge,
                primaryAction: commercialFollowUpMeta.value.primaryAction,
                secondaryAction: commercialFollowUpMeta.value.secondaryAction,
            }],
        };
    }

    if (cardId === 'stale-contact') {
        const latestConversation = communicationTimeline.value[0];
        const items: ClientOverviewAttentionDialogItem[] = [];

        if (latestConversation) {
            items.push({
                id: latestConversation.id,
                title: latestConversation.title || 'Review latest conversation',
                presentation: 'message',
                message: {
                    title: latestConversation.title || 'Latest conversation',
                    actorName: latestConversation.actorName,
                    channelLabel: communicationChannelLabel(latestConversation.channel),
                    body: summarizedOverviewText(
                        latestConversation.body,
                        'This client has gone quiet and likely needs a deliberate follow-up.',
                    ),
                    timestamp: latestConversation.at,
                },
                metaChips: [
                    {
                        label: `Last contact was ${daysSinceLastContact.value} day${daysSinceLastContact.value === 1 ? '' : 's'} ago`,
                        tone: 'warning',
                    },
                    ...(latestConversation.actorName ? [{
                        label: `Last handled by ${latestConversation.actorName}`,
                        tone: 'muted' as const,
                    }] : []),
                ],
                primaryAction: { kind: 'tab', label: 'Open comms workspace', tab: 'comms' },
            });
        }

        if (waitingClientTasks.value[0]) {
            items.push({
                id: `stale-follow-up:${waitingClientTasks.value[0].id}`,
                title: 'Review blocked client task',
                presentation: 'thread',
                subtitle: relatedProjectNameById(waitingClientTasks.value[0].projectId) || waitingClientTasks.value[0].client || 'Client task',
                thread: overviewTaskThreadPresentation(waitingClientTasks.value[0]),
                metaChips: overviewTaskMetaChips(waitingClientTasks.value[0]),
                badge: 'Blocked work',
                primaryAction: { kind: 'task', label: 'Open task here', taskId: waitingClientTasks.value[0].id },
                secondaryAction: projectTaskHref(waitingClientTasks.value[0])
                    ? { kind: 'href', label: 'Open in project', href: projectTaskHref(waitingClientTasks.value[0]) || undefined }
                    : { kind: 'tab', label: 'Open delivery queue', tab: 'delivery' },
            });
        }

        if (!items.length) {
            items.push({
                id: 'stale-follow-up',
                title: primaryClientContact.value?.name
                    ? `Reach back out to ${primaryClientContact.value.name}`
                    : 'Restart the conversation',
                presentation: 'summary',
                summary: 'This client has gone quiet and would benefit from a deliberate follow-up from the comms workspace.',
                detailFields: [
                    {
                        label: 'Best route',
                        value: primaryClientContact.value?.email
                            || primaryClientContact.value?.phone
                            || 'No primary contact route saved',
                    },
                    {
                        label: 'Last contact',
                        value: `${daysSinceLastContact.value} day${daysSinceLastContact.value === 1 ? '' : 's'} ago`,
                    },
                ],
                metaChips: [
                    {
                        label: 'No recent communication thread to review yet',
                        tone: 'warning',
                    },
                ],
                badge: 'Follow-up',
                primaryAction: { kind: 'tab', label: 'Open comms workspace', tab: 'comms' },
            });
        }

        return {
            title: 'No recent contact',
            description: 'Use this list to decide whether the next move is a follow-up, a blocked task, or a conversation review.',
            emptyTitle: 'No stale follow-up actions',
            emptyDescription: 'Fresh contact will keep this card quiet.',
            items,
        };
    }

    if (cardId === 'overdue-invoice') {
        return {
            title: 'Outstanding payment risk',
            description: 'These invoices are overdue and may need a finance follow-up before they become a delivery risk.',
            emptyTitle: 'No overdue invoices',
            emptyDescription: 'Overdue finance items will surface here when they need attention.',
            items: outstandingInvoices.value
                .filter((invoice) => String(invoice.status || '').toLowerCase() === 'overdue')
                .sort((left, right) => toMillis(left.dueDate) - toMillis(right.dueDate))
                .map((invoice) => ({
                    id: invoice.id,
                    title: invoice.title || invoice.invoiceNumber || 'Overdue invoice',
                    presentation: 'invoice' as const,
                    invoice: {
                        invoice,
                        projectName: relatedProjectNameById(invoice.projectId),
                        currency: clientCurrency.value,
                    },
                    metaChips: [
                        {
                            label: invoice.dueDate
                                ? `Overdue since ${formatDate(invoice.dueDate)}`
                                : 'No due date recorded',
                            tone: 'warning' as const,
                        },
                        {
                            label: `${formatMoney(Number(invoice.amountDue || 0), invoice.currency || clientCurrency.value)} due`,
                            tone: 'muted' as const,
                        },
                    ],
                    primaryAction: { kind: 'tab', label: 'Open finance workspace', tab: 'finance' },
                    secondaryAction: projectFinanceHref(invoice)
                        ? { kind: 'href', label: 'Open in project', href: projectFinanceHref(invoice) || undefined }
                        : undefined,
                })),
        };
    }

    return {
        title: 'Client is steady',
        description: 'Nothing urgent is calling for intervention, so use these lighter actions to keep context current without cluttering the page.',
        emptyTitle: 'No suggestions available',
        emptyDescription: 'Lightweight next steps will appear here when this client needs them.',
        items: [
            {
                id: 'steady-notes',
                title: 'Review notes and handoff',
                presentation: 'summary',
                summary: 'Keep the handoff current so the next teammate can understand the client quickly.',
                detailFields: [
                    {
                        label: 'Workspace',
                        value: 'Notes and handoff',
                    },
                    {
                        label: 'Why now',
                        value: 'Keep the next teammate oriented without opening the full editor by default.',
                    },
                ],
                badge: 'Notes',
                primaryAction: { kind: 'dialog', label: 'Open notes', dialog: 'notes' },
            },
            {
                id: 'steady-comms',
                title: 'Check communication workspace',
                presentation: 'summary',
                summary: 'Review the thread history or send a follow-up if this client needs a small nudge.',
                detailFields: [
                    {
                        label: 'Workspace',
                        value: 'Comms',
                    },
                    {
                        label: 'Best for',
                        value: 'Reviewing recent threads, touchpoints, and reply drafts in one place.',
                    },
                ],
                badge: 'Comms',
                primaryAction: { kind: 'tab', label: 'Open comms workspace', tab: 'comms' },
            },
            {
                id: 'steady-controls',
                title: 'Manage client controls',
                presentation: 'summary',
                summary: 'Lifecycle changes and configuration stay available here without living on the overview surface.',
                detailFields: [
                    {
                        label: 'Workspace',
                        value: 'Client controls',
                    },
                    {
                        label: 'Best for',
                        value: 'Updating lifecycle, ownership, and supporting configuration when the client changes.',
                    },
                ],
                badge: 'Controls',
                primaryAction: { kind: 'dialog', label: 'Open controls', dialog: 'controls' },
            },
        ],
    };
});


    return {
        commercialFollowUpMeta,
        overviewAttentionCards,
        overviewActiveProjects,
        overviewAttentionDialogState,
        relatedProjectNameById,
        formatHumanValue,
    };
}
